import type { Line, ReminderConfig } from "@/lib/types";

export function reminderFireKey(line: Line): string {
  return `fired:${line.id}:${line.scheduledDate}`;
}

// リマインダーが発火すべきか判定する。
// 「予定日の当日、指定時刻以降」のみ true（過去日・未来日は発火しない）。
// userId を渡すと、他メンバーに割り当てられたタスク（assigneeUserId が自分以外）は発火しない。
// assigneeUserId 未設定のタスクは常に発火する（ソロワークスペースでは設定されないため）。
export function isReminderDue(
  line: Line,
  now: Date,
  fired: Set<string>,
  userId?: string
): boolean {
  if (line.type !== "task") return false;
  if (line.status === "completed") return false;
  if (!line.reminder?.time || !line.scheduledDate) return false;
  if (line.assigneeUserId && line.assigneeUserId !== userId) return false;
  if (fired.has(reminderFireKey(line))) return false;

  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const todayYMD = `${y}-${m}-${d}`;
  if (line.scheduledDate !== todayYMD) return false;

  const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  return hhmm >= line.reminder.time;
}

// 7日より古い発火記録キーを削除する（キー形式: fired:{lineId}:{YYYY-MM-DD}）
export function pruneFiredKeys(keys: string[], todayYMD: string): string[] {
  const cutoff = new Date(todayYMD + "T00:00:00");
  cutoff.setDate(cutoff.getDate() - 7);
  const cy = cutoff.getFullYear();
  const cm = String(cutoff.getMonth() + 1).padStart(2, "0");
  const cd = String(cutoff.getDate()).padStart(2, "0");
  const cutoffYMD = `${cy}-${cm}-${cd}`;
  return keys.filter((key) => {
    const date = key.split(":")[2];
    return !date || date >= cutoffYMD;
  });
}

/**
 * DB の JSONB 値を ReminderConfig として安全に解釈する。
 * 不正な値は undefined(クラッシュさせない)。parseRepeatFromRow と同じパターン。
 */
export function parseReminderFromRow(value: unknown): ReminderConfig | undefined {
  let obj: unknown = value;
  if (typeof value === "string") {
    try {
      obj = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return undefined;
  const rec = obj as Record<string, unknown>;
  if (typeof rec.time !== "string") return undefined;
  return rec as unknown as ReminderConfig;
}

/**
 * ReminderConfig の内容比較。sync-manager の差分検出で参照比較の代わりに使う。
 * (repeatConfigsEqual と同じ理由: キー順が安定しているため JSON 比較で十分)
 */
export function reminderConfigsEqual(a?: ReminderConfig, b?: ReminderConfig): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}
